<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ScenariosAPI from 'dashboard/api/scenarios';
import Button from 'dashboard/components-next/button/Button.vue';
import { appointmentMessages } from './appointmentMessages';

const props = defineProps({ modelValue: { type: Array, default: () => [] } });
const emit = defineEmits(['update:modelValue']);
const { t } = useI18n({ useScope: 'local', messages: appointmentMessages });
const catalog = ref({});
const failed = ref(false);
const groups = computed(() =>
  ['patient', 'appointment', 'appointment_service'].map(context => ({
    context,
    fields: (catalog.value[context] || []).filter(
      field =>
        field.editor_visible &&
        field.type === 'text' &&
        !field.references &&
        !field.is_date
    ),
  }))
);
onMounted(async () => {
  try {
    catalog.value =
      (await ScenariosAPI.nodeCatalog()).data.condition_fields || {};
  } catch {
    failed.value = true;
  }
});
const update = (index, values) =>
  emit(
    'update:modelValue',
    props.modelValue.map((rule, i) =>
      i === index ? { ...rule, ...values } : rule
    )
  );
const add = () =>
  emit('update:modelValue', [
    ...props.modelValue,
    {
      context: 'appointment',
      field: '',
      values: [],
      match: 'all',
      effect: 'append',
      scope: 'common',
      text: '',
    },
  ]);
const choose = (index, value) => {
  const [context, field] = value.split('.');
  update(index, { context, field });
};
</script>

<template>
  <section class="pt-5 border-t border-n-weak space-y-3">
    <h2 class="text-sm font-medium">{{ t('preparation') }}</h2>
    <p class="text-xs leading-5 text-n-slate-11">{{ t('preparationHint') }}</p>
    <p v-if="failed" role="alert" class="text-xs text-n-ruby-11">
      {{ t('fieldsFailed') }}
    </p>
    <div
      v-for="(rule, index) in modelValue"
      :key="index"
      class="p-3 border border-n-weak rounded-lg space-y-3"
    >
      <label class="block text-xs"
        >{{ t('parameter') }}
        <select
          :value="`${rule.context}.${rule.field}`"
          class="w-full mt-1"
          @change="choose(index, $event.target.value)"
        >
          <option value="appointment.">{{ t('parameter') }}</option>
          <optgroup
            v-for="group in groups"
            :key="group.context"
            :label="t(group.context)"
          >
            <option
              v-for="field in group.fields"
              :key="field.name"
              :value="`${group.context}.${field.name}`"
            >
              {{ field.label }}
            </option>
          </optgroup>
        </select>
      </label>
      <div class="grid sm:grid-cols-2 gap-3">
        <label class="text-xs"
          >{{ t('values')
          }}<input
            :value="rule.values.join(';')"
            class="w-full mt-1"
            :placeholder="t('valueExample')"
            @input="
              update(index, {
                values: $event.target.value
                  .split(';')
                  .map(v => v.trim())
                  .filter(Boolean),
              })
            "
        /></label>
        <label class="text-xs"
          >{{ t('condition')
          }}<select
            :value="rule.match"
            class="w-full mt-1"
            @change="update(index, { match: $event.target.value })"
          >
            <option value="all">{{ t('all') }}</option>
            <option value="any">{{ t('any') }}</option>
          </select></label
        >
      </div>
      <div class="grid sm:grid-cols-2 gap-3">
        <select
          :aria-label="t('preparation')"
          :value="rule.scope"
          @change="update(index, { scope: $event.target.value })"
        >
          <option value="common">{{ t('common') }}</option>
          <option value="appointment">{{ t('specific') }}</option>
        </select>
        <select
          :aria-label="t('replace')"
          :value="rule.effect"
          @change="update(index, { effect: $event.target.value })"
        >
          <option value="append">{{ t('append') }}</option>
          <option value="replace">{{ t('replace') }}</option>
        </select>
      </div>
      <label class="block text-xs"
        >{{ t('text')
        }}<textarea
          :value="rule.text"
          rows="2"
          class="w-full mt-1"
          @input="update(index, { text: $event.target.value })"
        />
      </label>
      <Button
        :label="t('removeRule')"
        size="sm"
        color="slate"
        variant="ghost"
        icon="i-lucide-trash-2"
        @click="
          emit(
            'update:modelValue',
            modelValue.filter((_, i) => i !== index)
          )
        "
      />
    </div>
    <p
      v-if="modelValue.some(rule => rule.effect === 'replace')"
      class="text-xs leading-5 text-n-slate-11"
    >
      {{ t('replacementHint') }}
    </p>
    <Button
      :label="t('addRule')"
      size="sm"
      variant="ghost"
      icon="i-lucide-plus"
      :disabled="modelValue.length >= 30 || failed"
      @click="add"
    />
  </section>
</template>
